import { useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { PermissionGroupApp, TotadminPermission, useCreatePermissionGroupMutation } from 'data/__generated__';

import { TotemInput } from 'components/TotemInput';
import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';

import { toSlug } from '../constants/permissionGroupApps';

/**
 * Crée un groupe VIDE (hors la porte d'entrée) : la composition se fait ensuite sur la fiche, avec la
 * grille complète. Seuls les groupes totadmin se créent ici — les autres apps restent sur leurs rôles de code.
 */
export function PermissionGroupPopup({ isOpen, setIsOpen }: { isOpen: boolean; setIsOpen: (arg: boolean) => void }) {
    const navigate = useNavigate();
    const [label, setLabel] = useState<string>('');
    const [slug, setSlug] = useState<string>('');
    const [isSlugEdited, setIsSlugEdited] = useState<boolean>(false);
    const [description, setDescription] = useState<string>('');

    const [createPermissionGroup, { loading }] = useCreatePermissionGroupMutation({
        refetchQueries: ['GET_PERMISSION_GROUPS'],
    });

    const reset = () => {
        setLabel('');
        setSlug('');
        setIsSlugEdited(false);
        setDescription('');
    };

    const handleClose = () => {
        setIsOpen(false);
        reset();
    };

    const handleLabelChange = (value: string) => {
        setLabel(value);
        if (!isSlugEdited) {
            setSlug(toSlug(value));
        }
    };

    const handleSubmit = async () => {
        try {
            const { data } = await createPermissionGroup({
                variables: {
                    input: {
                        slug,
                        label,
                        description,
                        app: PermissionGroupApp.Totadmin,
                        permissions: [TotadminPermission.TotadminAccess],
                    },
                },
            });
            const result = data?.createPermissionGroup;

            if (!result?.success || !result.permissionGroup) {
                toast.error(result?.error ?? 'Une erreur est survenue lors de la création du groupe');
                return;
            }

            toast.success('Groupe créé');
            handleClose();
            navigate(`/permission-group/${result.permissionGroup.slug}`);
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Une erreur est survenue');
        }
    };

    return (
        <TotemPopup title="Créer un groupe de permissions" isOpen={isOpen} setIsOpen={handleClose}>
            <Fields>
                <TotemInput
                    label="Nom du groupe"
                    sublabel="Ce que fait ce profil (ex. « Niveau 3 — Remboursements »)"
                    onChange={handleLabelChange}
                    placeholder="Nom"
                    value={label}
                    dataTest="permission-group-label"
                    autoFocus
                />
                <TotemInput
                    label="Identifiant"
                    sublabel="Stocké tel quel sur les utilisateurs : minuscules, chiffres et tirets. Ne se modifie plus ensuite."
                    onChange={(value) => {
                        setIsSlugEdited(true);
                        setSlug(value);
                    }}
                    placeholder="niveau-3-remboursements"
                    value={slug}
                    dataTest="permission-group-slug"
                />
                <TotemInput
                    label="Description"
                    onChange={setDescription}
                    placeholder="Facultatif"
                    value={description}
                />
                <TotemPrimaryLoadingButton
                    onClick={handleSubmit}
                    disabled={!label.trim() || !slug}
                    loading={loading}
                    useIcon={false}
                    label="Créer le groupe"
                    data-test="permission-group-submit"
                />
            </Fields>
        </TotemPopup>
    );
}

const Fields = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 15px;

    > :not(:first-child) {
        margin-top: 15px;
    }
`;
